// Synthesized sound effects — Web Audio only, no asset files.
//
// Every effect is built from oscillators/noise at call time, so the whole
// sound system costs zero network bytes and stays trivially tweakable. All
// effects are short (≤600ms) and mixed well below full scale; they're meant
// to register as tactile feedback, not draw attention to themselves.
//
// Autoplay policy: browsers only allow an AudioContext to start (or resume)
// inside a user gesture. `unlock()` is wired to the first pointerdown in
// main.ts; sounds triggered before that (or while the tab is muted by the
// browser) are silently dropped.

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let enabled = true;

function getCtx(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  if (!ctx) {
    try {
      ctx = new AudioContext();
      master = ctx.createGain();
      master.gain.value = 0.5;
      master.connect(ctx.destination);
    } catch {
      return null;
    }
  }
  return ctx;
}

/** Resume the context from within a user gesture. Safe to call repeatedly. */
export function unlockAudio(): void {
  const c = getCtx();
  if (c && c.state === "suspended") void c.resume().catch(() => {});
}

export function setSoundEnabled(on: boolean): void {
  enabled = on;
}

export function isSoundEnabled(): boolean {
  return enabled;
}

/** True when we can actually emit sound right now. */
function ready(): AudioContext | null {
  if (!enabled) return null;
  const c = getCtx();
  if (!c || c.state !== "running" || !master) return null;
  return c;
}

// Shared 0.5s noise buffer, generated once.
let noiseBuf: AudioBuffer | null = null;
function noise(c: AudioContext): AudioBuffer {
  if (!noiseBuf || noiseBuf.sampleRate !== c.sampleRate) {
    noiseBuf = c.createBuffer(1, Math.floor(c.sampleRate * 0.5), c.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  }
  return noiseBuf;
}

/** One filtered noise tap — the building block for clicks and clatter. */
function noiseTap(
  c: AudioContext,
  at: number,
  opts: { freq: number; q?: number; gain: number; decay: number },
): void {
  const src = c.createBufferSource();
  src.buffer = noise(c);
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = opts.freq;
  bp.Q.value = opts.q ?? 1.2;
  const g = c.createGain();
  g.gain.setValueAtTime(opts.gain, at);
  g.gain.exponentialRampToValueAtTime(0.001, at + opts.decay);
  src.connect(bp).connect(g).connect(master!);
  src.start(at, Math.random() * 0.3);
  src.stop(at + opts.decay + 0.02);
}

/** One short pitched blip. */
function tone(
  c: AudioContext,
  at: number,
  opts: { freq: number; type?: OscillatorType; gain: number; decay: number; glideTo?: number },
): void {
  const o = c.createOscillator();
  o.type = opts.type ?? "sine";
  o.frequency.setValueAtTime(opts.freq, at);
  if (opts.glideTo !== undefined) {
    o.frequency.exponentialRampToValueAtTime(opts.glideTo, at + opts.decay);
  }
  const g = c.createGain();
  g.gain.setValueAtTime(opts.gain, at);
  g.gain.exponentialRampToValueAtTime(0.001, at + opts.decay);
  o.connect(g).connect(master!);
  o.start(at);
  o.stop(at + opts.decay + 0.02);
}

/** Dice tumbling: a burst of irregular clicks fading out, then a settle knock. */
export function playDiceRoll(): void {
  const c = ready();
  if (!c) return;
  const t0 = c.currentTime;
  let t = t0;
  const taps = 6;
  for (let i = 0; i < taps; i++) {
    const fade = 1 - i / taps;
    noiseTap(c, t, { freq: 2300 + Math.random() * 1400, gain: 0.16 * fade + 0.04, decay: 0.045 });
    // Body knock underneath the click — the "wood" of the tumble.
    tone(c, t, { freq: 210 + Math.random() * 90, type: "triangle", gain: 0.05 * fade, decay: 0.05 });
    t += 0.035 + Math.random() * 0.05 + i * 0.008;
  }
  // Final settle: slightly stronger double-knock.
  noiseTap(c, t, { freq: 1900, gain: 0.2, decay: 0.06 });
  tone(c, t, { freq: 170, type: "triangle", gain: 0.09, decay: 0.08 });
}

/** Checker landing on a point — soft click + low thump. */
export function playCheckerPlace(): void {
  const c = ready();
  if (!c) return;
  const t = c.currentTime;
  noiseTap(c, t, { freq: 1700, q: 1.6, gain: 0.14, decay: 0.05 });
  tone(c, t, { freq: 190, type: "sine", gain: 0.1, decay: 0.07 });
}

/** Blot hit — brighter two-step descending sting so it stands out from a plain landing. */
export function playHit(): void {
  const c = ready();
  if (!c) return;
  const t = c.currentTime;
  noiseTap(c, t, { freq: 2600, q: 1.4, gain: 0.18, decay: 0.06 });
  tone(c, t, { freq: 620, type: "square", gain: 0.06, decay: 0.1, glideTo: 330 });
  tone(c, t + 0.07, { freq: 300, type: "triangle", gain: 0.09, decay: 0.12, glideTo: 190 });
}

/** Rejected input — one quiet low thud. */
export function playDenied(): void {
  const c = ready();
  if (!c) return;
  const t = c.currentTime;
  tone(c, t, { freq: 130, type: "sine", gain: 0.1, decay: 0.09, glideTo: 88 });
}

/** Game won — short warm arpeggio. */
export function playWin(): void {
  const c = ready();
  if (!c) return;
  const t = c.currentTime;
  const notes = [392, 494, 587, 784]; // G4 B4 D5 G5
  notes.forEach((f, i) => {
    tone(c, t + i * 0.11, { freq: f, type: "triangle", gain: 0.11, decay: 0.32 });
  });
  // Soft sustaining root underneath.
  tone(c, t, { freq: 196, type: "sine", gain: 0.07, decay: 0.6 });
}

/** Game lost — two soft descending tones, sympathetic rather than punishing. */
export function playLoss(): void {
  const c = ready();
  if (!c) return;
  const t = c.currentTime;
  tone(c, t, { freq: 330, type: "triangle", gain: 0.09, decay: 0.28 });
  tone(c, t + 0.16, { freq: 247, type: "triangle", gain: 0.09, decay: 0.4 });
}
